// @flow
import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {useLocation, useHistory} from 'react-router-dom';

import {
  makeStyles,
  CircularProgress,
  Button,
  ButtonGroup,
  Typography,
} from '@material-ui/core';

import {colors} from '../../../../../../assets/styles/colors';

import ConversationPreview from '../ConversationPreview/ConversationPreview';

import {
  useCurrentConversation,
  useSetInitialWhatsappConversations,
  useConversations,
  useFilterConversations,
  useConversationSelectedActions,
  useConversationSelected,
  useConversationsActions,
  useConversationsSynchronizing,
  useConversationIdSelected,
} from '../../../utils/conversation';

import type {Theme} from '@material-ui/core';
import FilterSection from './FilterSection';
import {useNearScreen, useDebounceEffect} from '../../../../../../utils/react';
import OpenConversation from './OpenConversation';
import {useStoreDevices} from '../../../utils';
import LoadingConversations from './LoadingConversations.jsx';
import {getDevicesService} from '../../../promises';
import {useNextTokenArchived} from '../../../utils/message';
import OpenConversationFromSearchParams from './OpenConversationFromSearchParams';
import CustomModal from '../../../../../../elements/CustomModal/CustomModal';
import WhatsAppIcon from '../../../../../../elements/icons/WhatsAppIcon';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';

type Props = {};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    width: '100%',
    borderRight: '1px solid #E8E8ED',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  },

  conversations: {
    borderTop: `1px solid ${colors.grey300}`,
    flexGrow: 1,
    overflow: 'auto',

    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  conversationsEmptyLoader: {
    color: colors.grey500,
    fontSize: 14,
    fontWeight: 500,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
    height: '100%',
  },
  conversationsEmpty: {
    color: colors.grey500,
    fontSize: 14,
    fontWeight: 500,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
    height: '100%',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    padding: '2rem',
  },
  footer: {
    width: '100%',
    borderTop: `1px solid ${colors.grey300}`,
  },
  actionButton: {
    color: colors.grey900,
    fontSize: '.875rem',
    width: '100%',
    textTransform: 'none',

    '&:hover': {
      backgroundColor: colors.grey100,
    },
  },
  overlayDisabled: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `${colors.grey100}80}`,
    zIndex: 1,
    cursor: 'not-allowed',
    backdropFilter: 'blur(2px)',
  },
  rootModal: {
    maxWidth: '600px !important',
    margin: '0 auto',
  },
  warningIcon: {
    fontSize: '3rem',
    width: 100,
    height: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    margin: '1rem auto',
    background: colors.success,
    color: 'white',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    textAlign: 'center',
  },
  message: {
    maxWidth: 300,
    textAlign: 'center',
  },
  options: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    width: '100%',
    margin: '1rem 0',
  },
  button: {
    padding: '10px 2px',
  },
}));

const MAX_CALLS = 10;

export default function ConversationsGrid(props: Props) {
  const classes = useStyles();
  const callCount = useRef(0);

  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const orderId = searchParams.get('orderId');
  const fromOnboarding = searchParams.get('fromOnboarding');

  const {
    isSynchronizingConversations,
    updateConversationSynchronizing,
  } = useConversationsSynchronizing();
  const {
    conversationId,
    resetConversationIdSelected,
  } = useConversationIdSelected();

  const [isDeviceSynchronized, setIsDeviceSynchronized] = useState(false);

  const {
    currentConversation,
    setCurrentConversation,
  } = useCurrentConversation();

  const {
    addPagination,
    loading,
    refreshConversations,
  } = useSetInitialWhatsappConversations();

  const conversations = useConversations();
  const {markAsRead} = useConversationsActions();

  const {
    toggleConversationSelected,
    closeSelectedConversations,
    markAsUnreadSelectedConversations,
  } = useConversationSelectedActions();
  const selectedConversations = useConversationSelected();
  const {
    handleResetNextTokenArchived,
    handleResetPrevTokenArchived,
  } = useNextTokenArchived();

  const {state, services} = useFilterConversations();

  const {fromRef, isNearScreen} = useNearScreen({
    distance: '200px',
    once: false,
    useAppRoot: true,
  });
  const {storeDevices, storeDevicesNotArchived} = useStoreDevices({
    refetchOnMount: true,
  });

  const whatsappDisconnectedDevice = useMemo(() => {
    if (storeDevicesNotArchived || !storeDevicesNotArchived.length)
      return false;

    const allDisabled = storeDevicesNotArchived.every(
      device =>
        device.state === 'DISCONNECTED' && device.service === 'WHATSAPP_WEB'
    );

    return allDisabled;
  }, [storeDevicesNotArchived]);

  useDebounceEffect(
    () => {
      if (isNearScreen) {
        addPagination();
      }
    },
    500,
    [addPagination, isNearScreen]
  );

  useEffect(() => {
    if (!isSynchronizingConversations) return;
    const interval = setInterval(() => {
      callCount.current += 1;

      if (isDeviceSynchronized) {
        updateConversationSynchronizing(false);
        clearInterval(interval);
      }

      if (callCount.current >= MAX_CALLS) {
        updateConversationSynchronizing(false);
        clearInterval(interval);
      }

      getDevicesService(services)
        .then(response => {
          const allDevicesSynchronized = response.data
            .filter(device => device.state === 'CONNECTED')
            .every(
              device => device.accountSynchronized && device.contactSynchronized
            );

          if (allDevicesSynchronized) {
            setIsDeviceSynchronized(allDevicesSynchronized);
            updateConversationSynchronizing(false);
            clearInterval(interval);
          }
        })
        .catch(err => {
          console.log(err);
        });
    }, 3000);

    return () => clearInterval(interval);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeviceSynchronized, isSynchronizingConversations]);

  useEffect(() => {
    if (isSynchronizingConversations === false) {
      refreshConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSynchronizingConversations]);

  useEffect(() => {
    if (conversationId) {
      const conversation = conversations.find(
        conversation => conversation.id === conversationId
      );

      if (conversation) {
        setCurrentConversation(conversation);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, setCurrentConversation]);

  const onToggleSelect = useCallback(
    conversation => {
      toggleConversationSelected(conversation);
    },
    [toggleConversationSelected]
  );

  const onClickConversation = useCallback(
    conversation => {
      setCurrentConversation(conversation);
      resetConversationIdSelected();
      handleResetNextTokenArchived();
      handleResetPrevTokenArchived();
      if (!conversation.read) {
        markAsRead(conversation.id);
      }
    },
    [
      setCurrentConversation,
      resetConversationIdSelected,
      handleResetNextTokenArchived,
      handleResetPrevTokenArchived,
      markAsRead,
    ]
  );

  console.log({isSynchronizingConversations, fromOnboarding});

  return (
    <div className={classes.root}>
      {whatsappDisconnectedDevice && (
        <div className={classes.overlayDisabled}></div>
      )}
      <FilterSection />
      <div className={classes.content}>
        <div className={classes.conversations}>
          {conversations
            .filter(conversation => {
              if (state === 'ALL') return true;
              return conversation.crmConversationState === state;
            })
            .filter(conversation => conversation.nextConversationId === null)
            .map((conversation, index) => (
              <ConversationPreview
                key={conversation.id}
                selected={
                  !!selectedConversations.find(
                    selected => selected.id === conversation.id
                  )
                }
                conversation={conversation}
                // $FlowFixMe
                currentDevice={storeDevices.find(
                  device =>
                    device.id === conversation.deviceId &&
                    device.state === 'CONNECTED'
                )}
                current={
                  !!currentConversation &&
                  conversation.id === currentConversation.id
                }
                onToggleSelect={onToggleSelect}
                onClick={onClickConversation}
              />
            ))}
          {loading && (
            <div className={classes.loader}>
              <CircularProgress />
            </div>
          )}
          <span ref={fromRef}></span>
          {conversations.length === 0 && (
            <div className={classes.conversationsEmpty}>
              No hay conversaciones
            </div>
          )}
        </div>

        {selectedConversations.length > 0 && (
          <footer className={classes.footer}>
            <ButtonGroup variant="text" color="default" fullWidth>
              <Button
                className={classes.actionButton}
                disabled={state === 'ALL'}
                onClick={closeSelectedConversations}
              >
                Cerrar Chat
              </Button>
              <Button
                className={classes.actionButton}
                onClick={markAsUnreadSelectedConversations}
              >
                Marcar no leído
              </Button>
            </ButtonGroup>
          </footer>
        )}

        {isSynchronizingConversations && !whatsappDisconnectedDevice && (
          <LoadingConversations />
        )}
        <OpenConversation />

        {conversations.length === 0 && orderId && (
          <OpenConversationFromSearchParams />
        )}
        <CustomModal
          open={!isSynchronizingConversations && fromOnboarding}
          handleCloseModal={() => {
            history.push('/chat/multi-chat');
          }}
          className={classes.rootModal}
        >
          <div className={classes.header}>
            <span className={classes.warningIcon}>
              <WhatsAppIcon />
            </span>
            <Typography variant="h4" className={classes.title}>
              Felicitaciones, <br /> has conectado tu whatsapp web con éxito.
            </Typography>
          </div>
          <Typography className={classes.message}>
            Los nuevos mensajes que recibas aparecerán aquí.
          </Typography>

          <div className={classes.options}>
            <CustomButton
              className={classes.button}
              variant="contained"
              color="default"
              onClick={() => {
                history.push('/chat/multi-chat');
              }}
              fullWidth
            >
              Cerrar y continuar
            </CustomButton>

            <CustomButton
              onClick={() => {
                history.push('/home');
              }}
              className={classes.button}
              variant="contained"
              fullWidth
            >
              Regresar a la configuración
            </CustomButton>
          </div>
        </CustomModal>
      </div>
    </div>
  );
}

ConversationsGrid.propTypes = {};
